import React, { useEffect, useState } from 'react';
import Table from 'ui-components/table/table';
import { getInboundFreightsColumn } from './getInboundFreightsColumn';
import ContentContainer from 'templates/content-container/contentContainer';
import { useInboundFreightByTerminal } from '../hooks/useInboundFreightByTerminal';
import { getTerminalsList, terminalSummarySelector } from 'slices/terminal-summary/terminalSummarySlice';
import { useDispatch, useSelector } from 'react-redux';
import LabeledTextInput from 'ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import XGSSelect, { XGSSelectOption } from 'ui-components/xgs-select/xgsSelect';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import { inboundFreightStatusOptions } from 'app/data/freights/model';
import { ReactComponent as TotalTruck } from '../../../images/total_truck.svg';
import { ReactComponent as Inbound } from '../../../images/truck_inbound.svg';
import { ReactComponent as Arrived } from '../../../images/truck_arrived.svg';
import { ReactComponent as Late } from '../../../images/truck_late.svg';
import { ReactComponent as Alert } from '../../../images/alert.svg';
import TerminalSummaryState from 'slices/terminal-summary/TerminalSummaryState';
import './inboundFreight.scss'
import { useInboundFreightCounts } from '../hooks/useInboundFreightCounts';
import Loading from 'ui-components/loading/loading';

const Filters = ({
  searchString,
  setSearchString,
  status,
  setStatus,
  isLoading,
  setSubmittedSearch,
  terminalOptions,
  terminal,
  setTerminal,
}: any) => {

  useEffect(() => {
    const handler =
      setTimeout(() =>
        setSubmittedSearch(searchString), 1000);
    return () => clearTimeout(handler);
  }, [searchString, setSubmittedSearch]);

  return (
    <>
      <div className="drivers-list__filter">
        <div className="drivers-list__filter__input">
          <LabeledTextInput
            name="searchString"
            label="Manifest:"
            labelMode={LabelModes.column}
            className="drivers-list__filter__input__input"
            value={searchString}
            onChange={(e) =>
              setSearchString(e.currentTarget.value)
            }
            type="text"
            placeholder="Search Manifest"
            clearable
            onClear={() => { setSearchString(""); setSubmittedSearch("") }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSubmittedSearch(e.currentTarget.value);
              }
            }}
          />
        </div>
        <div className="drivers-list__filter__select" >
          <label style={{ fontSize: "14px", lineHeight: "24px" }}>Origin Terminal:</label>
          <XGSSelect
            name="terminal"
            labelMode={LabelModes.column}
            value={terminal}
            isLoading={isLoading}
            onValueChange={(v: XGSSelectOption | null | undefined) => {
              if (v) {
                setTerminal(v);
              }
            }}
            placeholder="Select"
            options={terminalOptions}
          />
        </div>
        <div className="drivers-list__filter__select">
          <label style={{ fontSize: "14px", lineHeight: "24px" }}>Status:</label>
          <XGSSelect
            name="status"
            label="Status"
            labelMode={LabelModes.column}
            value={status}
            isLoading={isLoading}
            onValueChange={(v: XGSSelectOption | null | undefined) => {
              if (v) {
                setStatus(v);
              }
            }}
            placeholder="Select"
            options={inboundFreightStatusOptions}
          />
        </div>
        <div className="drivers-list__filter__button">
          <div className="drivers-list__filter__button__search">
            <Button
              theme={ButtonThemes.blue}
              onClick={() => {
                setSubmittedSearch(searchString);
              }}
              size={ButtonSizes.medium}
            >
              Search
            </Button>
            <Button
              theme={ButtonThemes.gray}
              onClick={() => {
                setSearchString("");
                setTerminal(null);
                setSubmittedSearch("");
                setStatus(null)
              }}
              type="button"
              size={ButtonSizes.medium}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

const getTileIcon = (type: string) => {
  switch (type) {
    case "TOTAL_TRUCKS":
      return <TotalTruck />;
    case "INBOUND":
      return <Inbound />;
    case "ARRIVED":
      return <Arrived />;
    case "LATE":
      return <Late />;
    case "ALERT":
      return <Alert />;
    default:
      return null;
  }
};

const SummaryTiles = ({ isLoading, inboundFreightCounts }: any) => {
  const summaryTiles = [
    {
      title: "Total Trucks",
      count: inboundFreightCounts?.totalTrucks,
      iconType: "TOTAL_TRUCKS",
    },
    {
      title: "Inbound",
      count: inboundFreightCounts?.inbound,
      iconType: "INBOUND",
    },
    {
      title: "Arrived",
      count: inboundFreightCounts?.arrived,
      iconType: "ARRIVED",
    },
    {
      title: "Late",
      count: inboundFreightCounts?.late,
      iconType: "LATE",
    },
    {
      title: "Alert",
      count: inboundFreightCounts?.alert,
      iconType: "ALERT",
    },
  ];
  return (
    <div className="inbound-freights">
      {summaryTiles.map(({ title, count, iconType }) => (
        <div key={iconType} className="inbound-freights__tile">
          <div className={`icon-container --type-${iconType.toLowerCase()}`}>
            {getTileIcon(iconType)}
          </div>

          <div>
            {isLoading ? <Loading isLoading={true} /> : <div className="inbound-freights__tile__count">
              {count?.toLocaleString()}
            </div>}
            <div className="inbound-freights__tile__text">
              {title}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const InboundFreights: React.FC = () => {
  const dispatch = useDispatch();
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);

  const [searchString, setSearchString] = useState<string>("");
  const [status, setStatus] = useState<XGSSelectOption | null>(null);
  const [terminal, setTerminal] = useState<XGSSelectOption | null>(null);
  const [terminalOptions, setTerminalOptions] = useState<{
    value: string;
    label: string;
  }[]>([]);
  const [submittedSearch, setSubmittedSearch] = useState<string>("");

  const { data: inboundFreights, isLoading } = useInboundFreightByTerminal(terminal?.label.toString(), status?.value?.toString(), submittedSearch);

  const { data: inboundFreightCounts, isLoading: isLoadingInboundFreightCounts } = useInboundFreightCounts(submittedSearch, terminal?.label?.toString(), status?.value?.toString());

  useEffect(() => {
    if (terminalSummaryState.terminallist) {
      const terminalOptions = terminalSummaryState.terminallist?.map((terminal: any) => ({
        value: terminal.terminalNumber.toString(),
        label: terminal.terminalName.toLowerCase().capitalize()
      })) || [];
      setTerminalOptions(terminalOptions);
    }
  }, [terminalSummaryState.terminallist]);

  useEffect(() => {
    dispatch(getTerminalsList(0, null, null, "ACTIVE", "XGS Terminal", true));
    // eslint-disable-next-line
  }, []);

  return <>
    <ContentContainer 
      title="Inbound Freight"
      tooltip={"This module is in Preview. Certain data and features may be incomplete or under development."}
      status={"PREVIEW"}
    >
      <Filters
        searchString={searchString}
        setSearchString={setSearchString}
        status={status}
        setStatus={setStatus}
        isLoading={isLoading}
        terminalOptions={terminalOptions}
        setTerminal={setTerminal}
        terminal={terminal}
        setSubmittedSearch={setSubmittedSearch}
      />
      <SummaryTiles isLoading={isLoadingInboundFreightCounts} inboundFreightCounts={inboundFreightCounts} />
      <div className='inbound-freight__table'>
        <Table
          columns={getInboundFreightsColumn()}
          data={inboundFreights ?? []}
          isLoading={isLoading}
          rowHeight={56}
          infiniteScroll
          responsive
        />
      </div>
    </ContentContainer>
  </>;
};

export default InboundFreights;
